import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Platform, TextInput, View, type TextInputProps } from 'react-native';
import { withUniwind } from 'uniwind';

import { cn } from './cn';
import { useFormSubmit } from './Form';
import { useInSheet } from './sheet-context';
import { Text } from './Text';

// Inside a bottom sheet, gorhom's `BottomSheetTextInput` registers the focused
// input so the detached sheet lifts above the keyboard. It renders a
// non-react-native input, so `withUniwind` re-applies our `className`-based
// styling (the same interop Uniwind does for built-in react-native components).
const SheetTextInput = withUniwind(BottomSheetTextInput) as unknown as typeof TextInput;

export interface TextFieldProps extends TextInputProps {
  /** The underlying input, e.g. to focus it programmatically. */
  ref?: Ref<TextInput>;
  label?: string;
  /** Node shown on the right of the label row (e.g. a "Forgot?" link). */
  labelRight?: ReactNode;
  /** Node rendered inside the field on the right (e.g. a Show/Hide toggle). */
  right?: ReactNode;
  helper?: string;
  error?: string;
  className?: string;
  inputClassName?: string;
}

export function TextField({
  // Destructured (not left in the `{...props}` spread) so the caller's ref
  // can't silently replace the internal one the autofocus handling needs.
  ref,
  label,
  labelRight,
  right,
  helper,
  error,
  className,
  inputClassName,
  onFocus,
  onBlur,
  autoFocus,
  onSubmitEditing,
  ...props
}: TextFieldProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const setInputRef = useCallback(
    (node: TextInput | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  // Single-line fields inside a <Form> submit on Enter/return via the form's handler;
  // an explicit onSubmitEditing always wins, and multiline fields keep Enter = newline.
  const formSubmit = useFormSubmit();
  const submit =
    onSubmitEditing ?? (props.multiline ? undefined : (formSubmit ?? undefined));

  // gorhom keyboard tracking is native-only; web has no software keyboard, so it
  // keeps the plain react-native input (and its existing web autofocus handling).
  const useSheetInput = useInSheet() && Platform.OS !== 'web';
  const Input = useSheetInput ? SheetTextInput : TextInput;

  // On web, RNW maps `autoFocus` to the native HTML autofocus, which scrolls the
  // field into view — and inside a portal'd bottom sheet / dialog that yanks the
  // whole page to the top. Focus the node ourselves with `preventScroll` instead.
  // Native keeps the built-in `autoFocus` (so the keyboard still opens).
  useEffect(() => {
    if (autoFocus && Platform.OS === 'web') {
      const node = inputRef.current as unknown as HTMLElement | null;
      node?.focus?.({ preventScroll: true });
    }
  }, [autoFocus]);
  const border = error
    ? 'border-alert'
    : focused
      ? 'border-signal'
      : 'border-border-strong';

  return (
    <View className={cn('gap-1.5', className)}>
      {label || labelRight ? (
        <View className="flex-row items-center justify-between">
          {label ? <Text variant="label">{label}</Text> : <View />}
          {labelRight}
        </View>
      ) : null}

      <View className="justify-center">
        <Input
          ref={setInputRef}
          autoFocus={Platform.OS === 'web' ? undefined : autoFocus}
          placeholderTextColorClassName="accent-faint"
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          onSubmitEditing={submit}
          className={cn(
            'rounded-xl border-[1.5px] bg-card px-4 py-3 font-sans text-base text-foreground',
            border,
            right ? 'pr-16' : undefined,
            inputClassName,
          )}
          {...props}
        />
        {right ? <View className="absolute right-3">{right}</View> : null}
      </View>

      {error ? (
        <Text variant="caption" className="text-alert">
          {error}
        </Text>
      ) : helper ? (
        <Text variant="caption">{helper}</Text>
      ) : null}
    </View>
  );
}
